/**
 * "Sync from the customer's network" control for a network integration.
 *
 * WHY THIS EXISTS
 * Integration syncs run from the appliance. A controller whose management
 * address is private (UniFi on 192.168.x.x, a standalone Omada, anything not
 * published to the internet) is simply unreachable that way — and the failure
 * surfaced as a connect timeout naming the login URL, which reads like an
 * authentication problem and sends people to the wrong screen entirely.
 *
 * Turning this on hands the sync to an agent that IS on that network.
 *
 * The copy does real work here. An operator hitting a timeout has no reason to
 * suspect network topology, so the control explains the choice rather than just
 * offering it.
 */
import { useEffect, useState } from "react";

import api from "../api/client";

type CollectorDevice = { id: number; hostname: string; status: string; ip_address?: string | null };

export default function IntegrationCollector({
  customerId,
  supported,
  vendorLabel,
  pollViaAgent,
  collectorDeviceId,
  onChange,
}: {
  customerId: number;
  /** False for cloud vendors — a toggle that cannot work must not be offered. */
  supported: boolean;
  vendorLabel: string;
  pollViaAgent: boolean;
  collectorDeviceId: number | null;
  onChange: (next: { poll_via_agent: boolean; collector_device_id: number | null }) => void;
}) {
  const [devices, setDevices] = useState<CollectorDevice[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!supported || !pollViaAgent || !customerId) return;
    let cancelled = false;
    setLoading(true);
    api
      .get("/api/v1/devices", { params: { customer_id: customerId, page_size: 500 } })
      .then((r) => {
        if (cancelled) return;
        const rows = (r.data?.items || r.data || []) as any[];
        setDevices(
          rows
            .map((d) => ({
              id: d.id,
              hostname: d.hostname,
              status: d.status,
              ip_address: d.ip_address,
            }))
            // Online first, then by name: an offline pin would stall the sync,
            // so the ones that can actually do the job come first.
            .sort((a, b) =>
              a.status === b.status
                ? (a.hostname || "").localeCompare(b.hostname || "")
                : a.status === "online"
                  ? -1
                  : 1,
            ),
        );
      })
      .catch(() => setDevices([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [supported, pollViaAgent, customerId]);

  if (!supported) {
    return (
      <p className="text-[11px] text-gray-500 dark:text-gray-400">
        {vendorLabel} is a cloud API, so the appliance reaches it directly — no on-site
        collector needed.
      </p>
    );
  }

  const onlineCount = devices.filter((d) => d.status === "online").length;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={pollViaAgent}
          onChange={(e) =>
            onChange({
              poll_via_agent: e.target.checked,
              collector_device_id: e.target.checked ? collectorDeviceId : null,
            })
          }
          className="mt-0.5"
        />
        <span>
          <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            Sync from the customer's network
          </span>
          <span className="block text-[11px] text-gray-500 dark:text-gray-400 leading-snug mt-0.5">
            Turn this on when the controller's address is private (192.168.x.x, 10.x.x.x)
            and only reachable from inside the customer's site. An AeroGauge agent there
            runs the sync and reports back. Without it the appliance times out, because
            there is no route from here to there.
          </span>
        </span>
      </label>

      {pollViaAgent && (
        <div className="pl-6 space-y-1">
          <span className="block text-[11px] text-gray-600 dark:text-gray-300">
            Which agent
          </span>
          <select
            value={collectorDeviceId ?? ""}
            onChange={(e) =>
              onChange({
                poll_via_agent: true,
                collector_device_id: e.target.value ? Number(e.target.value) : null,
              })
            }
            className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600"
          >
            <option value="">
              Any online agent{onlineCount ? ` (${onlineCount} available)` : ""} —
              recommended
            </option>
            {devices.map((d) => (
              <option key={d.id} value={d.id}>
                {d.hostname}
                {d.ip_address ? ` — ${d.ip_address}` : ""}
                {d.status === "online" ? "" : " (offline)"}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
            {/* Stated because pinning looks like the careful choice and is
                usually the worse one — the sync dies with that machine. */}
            Leave this on "any" unless only one subnet can see the controller. A pinned
            machine that gets rebuilt or retired stops the sync silently.
          </p>
          {loading && (
            <p className="text-[11px] text-gray-400">Loading this customer's agents…</p>
          )}
          {!loading && devices.length === 0 && (
            <p className="text-[11px] text-amber-700 dark:text-amber-400">
              No agents found for this customer. Install one on the controller's network
              first, or the sync has nothing to run on.
            </p>
          )}
          {!loading && devices.length > 0 && onlineCount === 0 && (
            <p className="text-[11px] text-amber-700 dark:text-amber-400">
              None of this customer's agents are online right now. The sync will resume on
              its own when one checks in.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
